import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import { addGiftCardCategorySchema } from "components/Common/validation"
import {
  addGiftCardCategory,
  getGiftCardCategoryDetails,
  updateGiftCardCategory,
} from "store/actions/giftCardCategoriesAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"

const initialValues = {
  cardType: "",
  name: "",
  nameAr: "",
  description: "",
  descriptionAr: "",
  active: false,
}

const AddGiftCardCategory = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Gift Card Category "
  )}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const cardType = searchParams.get("cardType")

  const { loading, isLoading, categoryDetails } = useSelector(
    state => state.GiftCardCategories
  )
  const { userRole, accountType } = useSelector(state => state.Login)

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      37,
      "gift_card_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      37,
      "gift_card_3",
      accountType
    )

    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      getOldValues()
    }
  }, [])

  const getOldValues = () => {
    dispatch(getGiftCardCategoryDetails(cardType))
  }

  useEffect(() => {
    if (categoryDetails && !loading && mode === "edit") {
      const {
        cardName,
        cardNameAr,
        cardDescription,
        cardDescriptionAr,
        cardType,
        active,
      } = categoryDetails

      setFormValues({
        ...formValues,
        name: cardName,
        nameAr: cardNameAr,
        description: cardDescription,
        descriptionAr: cardDescriptionAr,
        cardType,
        active,
      })
    }
  }, [categoryDetails])

  const handleOnSubmit = values => {
    const { name, nameAr, description, descriptionAr, cardType, active } =
      values
    const payload = {
      cardName: name,
      cardNameAr: nameAr,
      cardDescription: description,
      cardDescriptionAr: descriptionAr,
      cardType: cardType,
      active: mode === "add" ? false : active,
    }

    if (mode === "add") {
      dispatch(addGiftCardCategory(payload)).then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status == true) {
          dispatch(
            showAlert({ text: "Category added successfully", type: "success" })
          )
          navigate(-1)
        } else {
          dispatch(
            showAlert({
              text: msg,
              type: "error",
            })
          )
        }
      })
    } else {
      const updatePayload = {
        ...payload,
        id: categoryDetails._id,
      }
      dispatch(updateGiftCardCategory(updatePayload)).then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(
            showAlert({
              text: "Category updated successfully",
              type: "success",
            })
          )
          navigate(-1)
        }
      })
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")}{" "}
                  {t("Gift Card Category")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addGiftCardCategorySchema()}
                  onSubmit={handleOnSubmit}
                >
                  {({ errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-12">
                          <div className="form_field">
                            <label htmlFor="cardType">
                              <span className="text-danger">*</span>{" "}
                              {t("Category Type")}
                            </label>
                            <Field name="cardType">
                              {({ field, form }) => (
                                <input
                                  type="text"
                                  id="cardType"
                                  {...field}
                                  placeholder={t("Enter Category Type")}
                                  className={`form-control ${
                                    form.touched.cardType &&
                                    form.errors.cardType
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  disabled={mode === "edit"}
                                  onKeyPress={e => {
                                    const pattern = /[a-zA-Z]/
                                    if (!pattern.test(e.key)) {
                                      e.preventDefault()
                                    }
                                  }}
                                  onChange={e => {
                                    const value = e.target.value.replace(
                                      /[^a-zA-Z]/g,
                                      ""
                                    )
                                    form.setFieldValue("cardType", value)
                                  }}
                                />
                              )}
                            </Field>
                            <TextError name="cardType" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="name" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="nameAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="nameAr"
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="nameAr" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="description">
                              {t("Description(English)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              id="description"
                              name="description"
                              placeholder={t("Description(English)")}
                              className={`form-control ${
                                touched.description && errors.description
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="descriptionAr">
                              {t("Description(عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              style={{ direction: "rtl" }}
                              id="descriptionAr"
                              name="descriptionAr"
                              placeholder={t("Description(عربي)")}
                              className={`form-control ${
                                touched.descriptionAr && errors.descriptionAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="form_field d-flex align-items-center">
                            <label className="mb-0 me-2">
                              {t("Active/Inactive")}
                            </label>
                            <Field name="active">
                              {({ field, form }) => (
                                <Switch
                                  isChecked={field.value}
                                  disabled={mode === "add"}
                                  onClickHandler={() => {
                                    form.setFieldValue("active", !field.value)
                                  }}
                                />
                              )}
                            </Field>
                          </div>
                        </div>
                        <div className="col-12 d-flex justify-content-end my-4">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>

                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddGiftCardCategory
